/**
 * O volume a que EU oiço cada pessoa — uma preferência minha, sobre outra pessoa.
 *
 * Mora no `localStorage` e não no servidor de propósito: "o Pedro fala alto
 * demais" é uma opinião do MEU par de colunas, não um facto sobre o Pedro. Se
 * fosse ao servidor, viajava para os outros dispositivos e para as calls onde o
 * problema não existe. É a mesma escolha que o Discord faz.
 *
 * Fica FORA do `VoicePrefs` (o push-to-talk) por uma razão mecânica, não
 * estética: o `VoiceStage` guarda uma cópia inteira das prefs em estado do React
 * e volta a gravá-la ao mexer no PTT. Um mapa de volumes lá dentro seria
 * apagado por essa cópia velha sempre que alguém mudasse um volume depois de a
 * página montar. Duas chaves, dois donos, zero corridas.
 */

const KEY = "gv.voice.volumes";

/** Ninguém nasce baixinho: 1 = o volume que o LiveKit dá por omissão. */
export const VOLUME_DEFAULT = 1;

/**
 * O tecto é 1 (100%) e não 2 como no Discord. Sem `webAudioMix` ligado nas
 * `RoomOptions`, o `setVolume` do LiveKit acaba num `element.volume = v`, e o
 * DOM atira `IndexSizeError` acima de 1 — um "200%" partia o áudio em vez de o
 * amplificar. Amplificar a sério é mudar o pipeline de áudio da sala inteira;
 * não é o que foi pedido.
 */
export const VOLUME_MAX = 1;

export type UserVolumes = Record<string, number>;

/** Fora de [0,1] não é preferência, é um valor corrompido — corta-se. */
export function clampVolume(value: number): number {
  if (!Number.isFinite(value)) return VOLUME_DEFAULT;
  return Math.min(VOLUME_MAX, Math.max(0, value));
}

/** Nunca atira: um `localStorage` cheio, desligado (modo privado) ou com lixo
 *  lá dentro não pode partir a entrada numa chamada. */
export function loadUserVolumes(): UserVolumes {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return {};
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== "object" || parsed === null || Array.isArray(parsed)) return {};
    const out: UserVolumes = {};
    for (const [id, value] of Object.entries(parsed as Record<string, unknown>)) {
      if (typeof value === "number") out[id] = clampVolume(value);
    }
    return out;
  } catch {
    return {};
  }
}

/** O volume guardado para alguém — ou 1, que é o "nunca mexi nisto". */
export function readUserVolume(userId: string): number {
  return loadUserVolumes()[userId] ?? VOLUME_DEFAULT;
}

/**
 * Grava, e APAGA a chave quando o valor volta ao normal. Sem isto o mapa só
 * cresce: cada pessoa com quem alguma vez se falou ficava lá para sempre a
 * dizer "100%", que é exactamente o que a ausência já dizia.
 */
export function writeUserVolume(userId: string, volume: number): number {
  const value = clampVolume(volume);
  try {
    const volumes = loadUserVolumes();
    if (value === VOLUME_DEFAULT) delete volumes[userId];
    else volumes[userId] = value;
    if (Object.keys(volumes).length === 0) localStorage.removeItem(KEY);
    else localStorage.setItem(KEY, JSON.stringify(volumes));
  } catch {
    // Preferência é conforto, não correcção: se não dá para gravar, o volume
    // vale para esta sessão e mais nada.
  }
  return value;
}
